// Global Instructions Rule Applied!
//
// Consolidated Funds grid: one row per fund group with the consolidated
// committed / closed / called / uncalled figures and a per-member pocket
// breakdown as expandable rows. Used in two places: the standalone
// /consolidated-funds page and as a second section below the funds list on
// the Funds screen (rendered only when groups exist). Row click opens the
// consolidated fund's detail tab.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Card, Spin, Table, Typography } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { TABLE_FUND_GROUPS } from '../../../state_manager/globals.ts'
import {
  FundGroupGridRow,
  FundGroupMemberStat
} from '../../../state_manager/data_models/fund_group.ts'
import { fetchFundGroups } from '../services/fundGroupsService.ts'

const { Text } = Typography

interface Props {
  /** Compact mode for embedding under the funds grid (hides when empty). */
  embedded?: boolean
}

export function ConsolidatedFundsSection({ embedded = false }: Props): React.ReactElement | null {
  const { openFormInTab } = useTabManagerContext()
  const [rows, setRows] = useState<FundGroupGridRow[]>([])
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchFundGroups(token)
      if (res.error) {
        if (!embedded) showErrorToast(res.error)
        return
      }
      setRows(res.data)
    } finally {
      setLoading(false)
      setLoaded(true)
    }
  }, [embedded])

  useEffect(() => {
    void load()
  }, [load])

  const openGroup = useCallback(
    (row: FundGroupGridRow) => {
      openFormInTab(TABLE_FUND_GROUPS, row.fund_groups__id, row.fund_groups__name ?? 'Consolidated Fund')
    },
    [openFormInTab]
  )

  const memberColumns = useMemo(
    () => [
      {
        title: 'Fund',
        key: 'fund',
        render: (_: unknown, m: FundGroupMemberStat) => (
          <span>
            {m.funds__name ?? '—'}
            {m.fund_group_members__is_primary ? <Text type='secondary'> (primary)</Text> : null}
          </span>
        )
      },
      {
        title: 'Committed (pocket)',
        key: 'committed',
        align: 'right' as const,
        render: (_: unknown, m: FundGroupMemberStat) => m.prog__committed_fmt ?? '-'
      },
      {
        title: 'Called to date',
        key: 'called',
        align: 'right' as const,
        render: (_: unknown, m: FundGroupMemberStat) => m.prog__called_to_date_fmt ?? '-'
      },
      {
        title: 'Uncalled',
        key: 'uncalled',
        align: 'right' as const,
        render: (_: unknown, m: FundGroupMemberStat) => m.prog__uncalled_fmt ?? '-'
      }
    ],
    []
  )

  const columns = useMemo(
    () => [
      {
        title: 'Consolidated Fund',
        key: 'name',
        fixed: 'left' as const,
        render: (_: unknown, row: FundGroupGridRow) => (
          <button
            type='button'
            className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
            onClick={() => openGroup(row)}
          >
            {row.fund_groups__name ?? '—'}
          </button>
        )
      },
      {
        title: 'Member Funds',
        key: 'members',
        render: (_: unknown, row: FundGroupGridRow) =>
          (row.prog__members ?? []).map((m) => m.funds__name).filter(Boolean).join(' + ') || '—'
      },
      {
        title: 'Committed',
        key: 'committed',
        align: 'right' as const,
        render: (_: unknown, row: FundGroupGridRow) => row.prog__committed_fmt ?? '-'
      },
      {
        title: 'Closed capital',
        key: 'closed',
        align: 'right' as const,
        render: (_: unknown, row: FundGroupGridRow) => row.prog__closed_capital_fmt ?? '-'
      },
      {
        title: 'Called to date',
        key: 'called',
        align: 'right' as const,
        render: (_: unknown, row: FundGroupGridRow) => row.prog__called_to_date_fmt ?? '-'
      },
      {
        title: 'Uncalled',
        key: 'uncalled',
        align: 'right' as const,
        render: (_: unknown, row: FundGroupGridRow) => row.prog__uncalled_fmt ?? '-'
      },
      {
        title: 'Investors',
        key: 'investors',
        align: 'right' as const,
        render: (_: unknown, row: FundGroupGridRow) => row.prog__investor_count ?? 0
      }
    ],
    [openGroup]
  )

  // Embedded on the Funds screen: render nothing until we know there are
  // groups (no empty shell for shops without consolidated funds).
  if (embedded && loaded && rows.length === 0) return null
  if (embedded && !loaded) return null

  return (
    <Card
      size='small'
      title='Consolidated Funds'
      extra={
        <Button icon={<ReloadOutlined />} size='small' onClick={() => void load()} disabled={loading}>
          Refresh
        </Button>
      }
    >
      {loading && rows.length === 0 ? (
        <div className='flex justify-center p-6'>
          <Spin />
        </div>
      ) : (
        <Table
          size='small'
          pagination={false}
          rowKey='fund_groups__id'
          dataSource={rows}
          columns={columns}
          scroll={{ x: 'max-content' }}
          expandable={{
            rowExpandable: (row: FundGroupGridRow) => (row.prog__members?.length ?? 0) > 0,
            expandedRowRender: (row: FundGroupGridRow) => (
              <Table
                size='small'
                pagination={false}
                rowKey={(m: FundGroupMemberStat) => String(m.funds__id ?? '')}
                dataSource={row.prog__members ?? []}
                columns={memberColumns}
              />
            )
          }}
        />
      )}
    </Card>
  )
}

export default ConsolidatedFundsSection
